import { Injectable } from '@angular/core';
import { applyNonceToElement } from 'core/utils/csp-nonce';
import { environment } from 'environment';
import { CookieService } from 'ngx-cookie-service';

export enum CookieCategories {
  C0001 = 'C0001',
  C0002 = 'C0002',
  C0003 = 'C0003',
  C0004 = 'C0004',
  C0005 = 'C0005',
}

export type CookieCategory =
  | CookieCategories.C0001
  | CookieCategories.C0002
  | CookieCategories.C0003
  | CookieCategories.C0004
  | CookieCategories.C0005;

export interface IConsentOneTrustEvent {
  timeStamp: number;
  detail: Array<CookieCategory>;
}

export enum ScriptTypeEnum {
  YOUTUBE_SCRIPT_ID = 'youtube-script',
  YOUTUBE_SCRIPT_ID_2 = 'www-widgetapi-script',
  SEGMENT_SCRIPT_ID = 'segment-script',
  TEN_COOKIE_CONSENT_SCRIPT_ID = 'ten-cookie-consent-script',
  COOKIE_CONSENT_SCRIPT_ID = 'cookie-consent-script',
  AUTO_BLOCK_COOKIE_SCRIPT_ID = 'auto-block-cookie-script',
  WOOTRIC_SCRIPT_Library = 'wootric-library-script',
  WOOTRIC_SCRIPT_ID = 'wootric-script',
}

@Injectable({
  providedIn: 'root',
})
export class ScriptService {
  private oneTrustKeys = {
    coutts: {
      staging: 'a5caff25-774f-4f73-bd57-3fedb3899265-test',
      production: 'a5caff25-774f-4f73-bd57-3fedb3899265',
    },
  };

  private scriptSrc: { [key: string]: string } = {
    [ScriptTypeEnum.YOUTUBE_SCRIPT_ID]: 'https://www.youtube.com/iframe_api',
    [ScriptTypeEnum.YOUTUBE_SCRIPT_ID_2]: 'https://www.youtube.com/iframe_api',
    [ScriptTypeEnum.AUTO_BLOCK_COOKIE_SCRIPT_ID]:
      'https://cdn-ukwest.onetrust.com/consent/9fcefc7a-c678-4db2-8d05-3d8ca896ee35-test/OtAutoBlock.js',
    [ScriptTypeEnum.TEN_COOKIE_CONSENT_SCRIPT_ID]: 'https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js',
    [ScriptTypeEnum.COOKIE_CONSENT_SCRIPT_ID]: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
    [ScriptTypeEnum.WOOTRIC_SCRIPT_Library]: 'https://cdn.wootric.com/wootric-sdk.js',
  };

  private dataDomainScript: string;
  private languageFromHTML: any;
  private segmentAppId: string;

  public constructor(private cookieService: CookieService) {}

  public appendScript(
    scriptId: ScriptTypeEnum,
    hasCookieSettingId: boolean,
    asyncLoad: boolean = true,
    innerHTML: string = ''
  ) {
    this.removeScript(scriptId);

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'text/javascript';
    script.async = asyncLoad;


    if (innerHTML) {
      script.innerHTML = innerHTML;
    }

    if (hasCookieSettingId) {
      script.type = 'text/plain';
      script.className = 'optanon-category-C0004';
    }

    if (this.scriptSrc[scriptId]) {
      script.src = this.scriptSrc[scriptId];
    }

    if ([ScriptTypeEnum.COOKIE_CONSENT_SCRIPT_ID, ScriptTypeEnum.TEN_COOKIE_CONSENT_SCRIPT_ID].includes(scriptId)) {
      script.setAttribute('data-domain-script', this.dataDomainScript);
      script.setAttribute('data-document-language', `${this.languageFromHTML}`);
      
      script.onload = () => {
        const eOT = new Event('OneTrustGroupsUpdated');
        document.dispatchEvent(eOT);
      };

      document.addEventListener('OneTrustGroupsUpdated', () => {
        window.addEventListener('consent.onetrust', (event) => {
          this.handleOnConsentChanged(this.segmentAppId, hasCookieSettingId, event as unknown as IConsentOneTrustEvent);
        });
      });
    }
    
    // Apply nonce using the enhanced logic that preserves existing values
    applyNonceToElement(script);
    document.head.insertBefore(script, document.head.firstElementChild);
  }

  public removeScript(scriptId: ScriptTypeEnum, cookies: Array<string> = []) {
    const script = this.getScript(scriptId);
    if (script) {
      document.head.removeChild(script);

      cookies.forEach((cookie) => {
        this.cookieService.delete(cookie);
        localStorage.removeItem(cookie);
      });
    }
  }

  public removeScripts(type: string) {
    const scripts = document.getElementsByTagName('script');
    const scriptsToRemove = [];

    for (let i = 0; i < scripts.length; i++) {
      const script = scripts[i];
      if (script.src.includes(type)) {
        scriptsToRemove.push(script);
      }
    }

    scriptsToRemove.forEach((script) => {
      document.head.removeChild(script);
      try {
        document.removeChild(script);
      } catch (e) {} // eslint-disable-line no-empty
    });
  }

  public appendYoutubeScript(hasCookieSettingId: boolean) {
    this.appendScript(ScriptTypeEnum.YOUTUBE_SCRIPT_ID, hasCookieSettingId);
  }

  private getScript(scriptId: string): HTMLElement | null {
    return document.getElementById(scriptId) || null;
  }

  public appendSegmentScript(hasCookieSettingId: boolean, segmentAppId: string): void {
    const innerHTML = `!(function () {
    var analytics = (window.analytics = window.analytics || []);
    if (!analytics.initialize) {
      if (analytics.invoked) {
        window.console &&
          console.error &&
          console.error("Segment snippet included twice.");
      } else {
        analytics.invoked = !0;
        analytics.methods = [
          "trackSubmit",
          "trackClick",
          "trackLink",
          "trackForm",
          "pageview",
          "identify",
          "reset",
          "group",
          "track",
          "ready",
          "alias",
          "debug",
          "page",
          "once",
          "off",
          "on",
        ];

        analytics.factory = function (t) {
          return function () {
            var e = Array.prototype.slice.call(arguments);
            e.unshift(t);
            analytics.push(e);
            return analytics;
          };
        };

        for (var t = 0; t < analytics.methods.length; t++) {
          var e = analytics.methods[t];
          analytics[e] = analytics.factory(e);
        }

        analytics.load = function (t) {
          var e = document.createElement("script");
          e.type = "text/javascript";
          e.async = !0;
          e.src =
            ("https:" === document.location.protocol ? "https://" : "http://") +
            "cdn.segment.com/analytics.js/v1/" +
            t +
            "/analytics.min.js";
          var n = document.getElementsByTagName("script")[0];
          n.parentNode.insertBefore(e, n);
        };

        analytics.SNIPPET_VERSION = "4.0.0";
        analytics.load("${segmentAppId}");
      }
    }
  })();`;

    this.appendScript(ScriptTypeEnum.SEGMENT_SCRIPT_ID, hasCookieSettingId, true, innerHTML);
  }

  public appendWootricScript(hasCookieSettingId: boolean, wootricAppId: string, memberEmail: string): void {
    const innerHTML = `window.wootricSettings = {
    email:'${memberEmail}', 
    created_at: 1234567890,  
    account_token: '${wootricAppId}',
  };

 
(function () {
    const script = document.createElement('script');
    script.src = 'https://cdn.wootric.com/wootric-sdk.js';
    script.async = true;
    script.onload = function () {
      if (typeof window.wootric === 'function') {
        window.wootric('run');
      } else {
        console.error('Wootric SDK did not initialize correctly.');
      }
    };
    document.head.appendChild(script);
  })();`;

    this.appendScript(ScriptTypeEnum.WOOTRIC_SCRIPT_ID, false, true, innerHTML);
  }

  public appendCookieConsentScript(
    segmentAppId: string,
    dataDomainScript: string,
    hasCookieSettingId: boolean,
    languageFromHTML: boolean,
    scriptId: ScriptTypeEnum,
    autoBlock = false
  ) {
    this.dataDomainScript = dataDomainScript;
    this.languageFromHTML = languageFromHTML;
    this.segmentAppId = segmentAppId;
    this.removeScript(scriptId);
    this.appendScript(scriptId, hasCookieSettingId, false);

    if (autoBlock) {
      this.appendScript(ScriptTypeEnum.AUTO_BLOCK_COOKIE_SCRIPT_ID, false, false);
    }
  }

  private handleOnConsentChanged(segmentAppId: string, hasCookieSettingId: boolean, event: IConsentOneTrustEvent) {
    if (event.detail.some((x) => x === CookieCategories.C0003)) {
      this.appendSegmentScript(hasCookieSettingId, segmentAppId);
    } else {
      this.removeScript(ScriptTypeEnum.SEGMENT_SCRIPT_ID, ['ajs_anonymous_id', 'ajs_user_id']);
      this.removeScripts('segment');
      this.removeScript(ScriptTypeEnum.WOOTRIC_SCRIPT_ID, []);
      this.removeScripts('wootric');
    }

    if (event.detail.some((x) => x === CookieCategories.C0004)) {
      this.appendYoutubeScript(hasCookieSettingId);
    } else {
      this.removeScript(ScriptTypeEnum.YOUTUBE_SCRIPT_ID, ['YSC', 'CONSENT', 'VISITOR_INFO1_LIVE']);
      this.removeScripts('youtube');
    }
  }

  public appendTenOneTrustCookieConsentScript(segmentAppId: string, cookieId: string, languageFromHTML: boolean) {
    // cookieId is coming from Django (siteSettings.cookie_setting_id)
    if (cookieId) {
      if (environment.production) {
        //don't add to test uat site
        if (location.host !== 'travel-test.mastercard.com') {
          this.appendCookieConsentScript(
            segmentAppId,
            cookieId,
            false,
            languageFromHTML,
            ScriptTypeEnum.TEN_COOKIE_CONSENT_SCRIPT_ID
          );
        }
      } else {
        this.appendCookieConsentScript(
          segmentAppId,
          cookieId,
          false,
          languageFromHTML,
          ScriptTypeEnum.TEN_COOKIE_CONSENT_SCRIPT_ID,
          true
        );
      }
    }
  }

  public appendOneTrustCookieConsentScript(
    segmentAppId: string,
    path: string,
    hasCookieSettingId: boolean,
    languageFromHTML: boolean
  ) {
    if (this.oneTrustKeys[path] === null || this.oneTrustKeys[path] === undefined) {
      console.warn(`[OneTrustCookieConsentScript] Invalid path`, path);
    } else {
      const env = environment.production ? 'production' : 'staging';
      const cookieId = this.oneTrustKeys[path][env];
      this.appendCookieConsentScript(
        segmentAppId,
        cookieId,
        hasCookieSettingId,
        languageFromHTML,
        ScriptTypeEnum.COOKIE_CONSENT_SCRIPT_ID
      );
    }
  }
}
